import React, { useEffect, useState, useCallback, useRef } from "react";
import { progressService } from "../../../../services/progressService";
import { videoRegistry } from "./index";
import VideoPlayer from "./VideoPlayer";
import QuestionPopup from "./QuestionPopup.jsx";
import ModuleQuestionPopup from "./ModuleQuestionPopup.jsx";
import { useModal } from "../../ModalContext";
import { questionRegistry } from "./questionRegistry.js";
import { apiFetch } from "../../../../services/apiFetch";

/* ---------------- Utils ---------------- */
function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

/* ---------------- Main Component ---------------- */
const VideoContainer = ({
  userId,
  course,
  module,
  subsection,
  onProgressUpdate,
  actions,
  onToggleImmersive,
}) => {
  const [activeTab, setActiveTab] = useState("about");
  const [notesData, setNotesData] = useState("");
  const [questionAnswer, setQuestionAnswer] = useState("");
  const [videoStatus, setVideoStatus] = useState("not_started");
  const [resumeTime, setResumeTime] = useState(0);

  const { openModal, closeModal } = useModal();

  const [videoUrls, setVideoUrls] = useState(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(true);

  const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

  /* ---------------- Fetch SAS Video URLs ---------------- */
  useEffect(() => {
    if (!subsection?.id) return;

    const fetchVideoUrls = async () => {
      try {
        setIsLoadingVideo(true);

        const res = await apiFetch(
          `${apiBaseURL}/subsections/${subsection.id}/video`
        );
        if (!res.ok) throw new Error("Failed to fetch video URLs");

        const data = await res.json();
        setVideoUrls(data);
      } catch (err) {
        console.error("Error loading video URLs:", err);
      } finally {
        setIsLoadingVideo(false);
      }
    };

    fetchVideoUrls();
  }, [subsection?.id, apiBaseURL]);

  /* ---------------- Reset on Subsection Change ---------------- */
  useEffect(() => {
    if (!subsection?.id) return;
    setActiveTab("about");
    setNotesData("");
    setQuestionAnswer("");
    setVideoStatus("not_started");
    setResumeTime(0);
  }, [subsection?.id]);

  /* ---------------- Load Progress ---------------- */
  useEffect(() => {
    if (!subsection?.id) return;

    const fetchProgressAndSetState = async () => {
      try {
        const progress = await progressService.getUserProgress(
          userId,
          subsection.id
        );

        const payload = progress?.data?.payload || progress?.payload || {};

        setNotesData(payload.notes || "");
        setQuestionAnswer(payload.questionAnswer || "");

        if (progress?.status) setVideoStatus(progress.status);

        // ⭐ Resume timestamp if exists
        if (payload.last_position) {
          setResumeTime(payload.last_position);
        }

        await progressService.updateUserState(userId, subsection.id);
      } catch (err) {
        console.error("Error loading progress:", err);
      }
    };

    fetchProgressAndSetState();
  }, [userId, subsection?.id]);

  /* ---------------- Toggle Immersive Mode ---------------- */
  useEffect(() => {
    onToggleImmersive(false);
  }, [onToggleImmersive]);

  /* ---------------- Save Progress ---------------- */
  const saveProgress = useCallback(
    async (status, payload = {}) => {
      try {
        const updated = await progressService.saveProgress(
          userId,
          subsection.id,
          { type: "video", payload },
          status
        );
        setVideoStatus(status);
        onProgressUpdate?.(updated);
      } catch (err) {
        console.error("Video progress save failed:", err);
      }
    },
    [userId, subsection?.id, onProgressUpdate]
  );

  /* ---------------- Debounced Notes Save ---------------- */
  const notesDebounceRef = useRef(null);

  useEffect(() => {
    notesDebounceRef.current = debounce((value) => {
      saveProgress("in_progress", {
        watched: videoStatus === "completed",
        notes: value,
        questionAnswer,
        last_position: resumeTime, // keep video resume updated
      });
    }, 1500);
  }, [saveProgress, questionAnswer, videoStatus, resumeTime]);

  const handleNotesChange = (e) => {
    const value = e.target.value;
    setNotesData(value);
    notesDebounceRef.current?.(value);
  };

  /* ---------------- Resume Timestamp Save (2s throttle) ---------------- */
  const timeSaveRef = useRef(null);

  useEffect(() => {
    timeSaveRef.current = debounce((pos) => {
      saveProgress("in_progress", {
        watched: videoStatus === "completed",
        notes: notesData,
        questionAnswer,
        last_position: pos,
      });
    }, 2000);
  }, [saveProgress, videoStatus, notesData, questionAnswer]);

  const handleTimeUpdate = (seconds) => {
    setResumeTime(seconds);
    timeSaveRef.current?.(seconds);
  };

  const goToNextSection = () => {
    // primary CTA already set OR fallback
    if (actions?.setPrimary) {
      document.getElementById("next-lesson-btn")?.click();
    }
  };

  /* ---------------- Video Metadata Component ---------------- */
  const VideoComponent = videoRegistry[subsection.subsection_url];
  if (!VideoComponent)
    return <p className="text-[#6B7280] italic">No matching video found.</p>;

  const videoComponentData = VideoComponent({
    subsection,
    saveProgress,
  });

  // const { about, transcript, relatedLinks, question } = videoComponentData;
  const { about, transcript } = videoComponentData;

  const resolvedQuestion =
  questionRegistry[subsection.subsection_url] || null;


  /* ---------------- Handle Video Completion ---------------- */
  const handleVideoComplete = async () => {
    await saveProgress("completed", { watched: true, last_position: 0 });

    // const hasCustomQuestion = !!question;
    const hasCustomQuestion = !!resolvedQuestion;

    if (hasCustomQuestion) {
      openModal(
        <ModuleQuestionPopup
          isOpen={true}
          onClose={closeModal}
          question={resolvedQuestion}
          initialAnswer={questionAnswer}
          onSubmit={async (answer) => {
            await saveProgress("completed", {
              watched: true,
              notes: notesData || "",
              questionAnswer: answer || questionAnswer || "",
              last_position: 0,
            });
            setQuestionAnswer(answer || questionAnswer || "");
            closeModal();
          }}
          onNext={goToNextSection} 
        />
      );
    } else {
      openModal(
        <QuestionPopup
          question={resolvedQuestion}
          initialAnswer={questionAnswer}
          onSubmit={async (answer) => {
            await saveProgress("completed", {
              watched: true,
              notes: notesData || "",
              questionAnswer: answer || questionAnswer || "",
              last_position: 0,
            });
            setQuestionAnswer(answer || questionAnswer || "");
            closeModal();
          }}
          onSkip={async () => {
            await saveProgress("completed", {
              watched: true,
              notes: notesData || "",
              questionAnswer,
              last_position: 0,
            });
            closeModal();
          }}
          onNext={goToNextSection}
        />
      );
    }

    if (actions) {
      actions.setPrimary({
        label: "Next Section",
        onClick: () =>
          document.getElementById("next-lesson-btn")?.click(),
        visible: true,
      });
    }
  };

  /* ---------------- Tabs ---------------- */
  const tabConfig = [
    { key: "about", label: "About", content: about },
    { key: "transcript", label: "Transcript", content: transcript },
    // { key: "links", label: "Related Links", content: relatedLinks },
    {
      key: "notes",
      label: "Notes",
      content: (
        <textarea
          className="w-full h-[250px] border border-gray-300 rounded-xl p-3 resize-none focus:ring-2 focus:ring-[#4EC6C1]"
          placeholder="Type your notes here..."
          value={notesData}
          onChange={handleNotesChange}
        ></textarea>
      ),
    },
  ];

  /* ---------------- Render ---------------- */
  return (
    <div className="w-full flex-1 min-h-0 overflow-y-auto scrollbar-hide">
      <h2 className="text-[28px] font-bold text-ink mb-6">
        {subsection.title}
      </h2>

      {/* Video Player */}
      <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-sm bg-black scrollbar-hide">
        {isLoadingVideo ? (
          <div className="w-full h-full flex items-center justify-center text-white text-lg">
            Loading video...
          </div>
        ) : !videoUrls ? (
          <div className="w-full h-full flex items-center justify-center text-red-500">
            Unable to load video.
          </div>
        ) : (
          <VideoPlayer
            key={subsection.id}
            mp4={videoUrls.mp4_url}
            webm={videoUrls.webm_url}
            resumeTime={resumeTime}
            onProgress={handleTimeUpdate}
            onComplete={handleVideoComplete}
            autoPlay={false}
            allowReplay={true}
          />
        )}
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <div className="flex items-center space-x-8 h-11">
          {tabConfig.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-2 text-sm leading-[1.5] transition-colors border-b-2 ${
                activeTab === tab.key
                  ? "text-brand border-brand font-semibold"
                  : "text-[#6B7280] border-transparent hover:text-ink"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="py-6 text-body leading-7 text-[#484848] space-y-4">
        {tabConfig.find((t) => t.key === activeTab)?.content}
      </div>
    </div>
  );
};

export default VideoContainer;
